import React, { PropsWithChildren, useContext } from 'react';

import { DndProvider, DndContext, DndProviderProps } from 'react-dnd';
import type { BackendFactory, DragDropManager } from 'dnd-core';
import { createDragDropManager } from 'dnd-core';

type Props<BackendContext, BackendOptions> = PropsWithChildren<
  DndProviderProps<BackendContext, BackendOptions>
>;

const backendManagerCache = new WeakMap<object, DragDropManager>();

export function SafeDndProvider<BackendContext = unknown, BackendOptions = unknown>({
  children,
  ...props
}: Props<BackendContext, BackendOptions>) {
  // Check if we're already inside a DndProvider by accessing the context
  const contextValue = useContext(DndContext);
  const existingManager = contextValue?.dragDropManager;

  // If we already have a drag-drop manager, don't create a new provider
  // This prevents multiple HTML5 backends from being initialized
  if (existingManager) {
    return <>{children}</>;
  }

  // Reuse a single manager per backend constructor to avoid
  // "cannot have two HTML5 backends at the same time" during route transitions.
  if ('backend' in props) {
    const backend = props.backend as unknown as BackendFactory;
    const backendKey = backend as unknown as object;
    let manager = backendManagerCache.get(backendKey);

    if (!manager) {
      manager = createDragDropManager(backend, props.context, props.options, props.debugMode);
      backendManagerCache.set(backendKey, manager);
    }

    return <DndProvider manager={manager}>{children}</DndProvider>;
  }

  // Only create a new DndProvider if one doesn't already exist
  return <DndProvider {...props}>{children}</DndProvider>;
}
